// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

// D-CRX-82 — pure helpers behind the "Emails & copies" card. The server re-validates everything;
// these only let the admin see a problem before pressing Save.

import type { EsignDocType } from '../api/esignSettings'

export const DOC_TYPE_LABELS: Record<EsignDocType, string> = {
  enrollment: 'Enrollment form',
  consent: 'Patient consent',
  pot: 'Plan of Treatment / Plan of Care',
}

/** Addresses typed one per line, or separated by commas/semicolons — trimmed, lower-cased, de-duplicated. */
export function parseAddresses(text: string): string[] {
  const out: string[] = []
  for (const raw of text.split(/[\n,;]+/)) {
    const e = raw.trim().toLowerCase()
    if (e && !out.includes(e)) out.push(e)
  }
  return out
}

export const addressesToText = (list: string[]) => list.join('\n')

export const domainOf = (email: string) => {
  const at = email.lastIndexOf('@')
  return at < 0 ? '' : email.slice(at + 1).toLowerCase()
}

export const isOffDomain = (email: string, pharmacyDomains: string[]) =>
  !pharmacyDomains.includes(domainOf(email))

const ADDRESS = /^[^\s@,;<>]+@[^\s@,;<>]+\.[^\s@,;<>]+$/
export const looksLikeAddress = (e: string) => ADDRESS.test(e)

/** Any {token} other than the allowed ones. */
export function unknownPlaceholders(text: string | null | undefined, allowed: string[]): string[] {
  if (!text) return []
  const found = text.match(/\{[^{}]*\}/g) ?? []
  return [...new Set(found.filter(t => !allowed.includes(t)))]
}

export interface SettingsLimits { addresses: number; subject: number; message: number }
const DEFAULT_LIMITS: SettingsLimits = { addresses: 10, subject: 100, message: 1000 }

/** The server's stable validation codes, in words. Limits come from the server's response. */
export function describeSettingsError(code: string, limits: SettingsLimits = DEFAULT_LIMITS): string {
  const [key, detail] = code.split(/:(.*)/s)
  switch (key) {
    case 'cc_emails_invalid': return `Copy recipient "${detail}" is not an email address.`
    case 'copy_emails_invalid': return `Notice recipient "${detail}" is not an email address.`
    case 'cc_emails_too_many': return `At most ${limits.addresses} copy recipients.`
    case 'copy_emails_too_many': return `At most ${limits.addresses} notice recipients.`
    case 'cc_emails_too_long': return 'The copy recipient list is too long to store — remove an address.'
    case 'copy_emails_too_long': return 'The notice recipient list is too long to store — remove an address.'
    case 'cc_emails_enabled_without_addresses': return 'Copies are switched on but no address is listed.'
    case 'copy_emails_enabled_without_addresses': return 'Notices are switched on but no address is listed.'
    case 'cc_offdomain_unconfirmed': return `${detail} is outside the pharmacy's email domain — confirm it before saving.`
    case 'email_subject_too_long': return `The subject is longer than ${limits.subject} characters.`
    case 'email_subject_too_long_filled':
      return `With the document and pharmacy names filled in, the subject is longer than ${limits.subject} characters — shorten it.`
    case 'email_subject_multiline': return 'The subject must be one line.'
    case 'email_message_too_long': return `The message is longer than ${limits.message.toLocaleString()} characters.`
    case 'placeholder_unknown': return `${detail} is not a placeholder. Use {document} or {pharmacy}.`
    case 'doc_type_invalid': return 'Unknown document type.'
    default: return code
  }
}
